import React from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router"

import { Page404 } from "@/components/nav/page404"
import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useAuthentication } from "@/contexts"
import { AdminEntityFieldsSkeleton } from "@/modules/admin/components/admin-entity-fields-skeleton"
import {
  canDeleteAdminUser,
  canMutateAdminUser,
  useAdminUserPageState,
} from "@/modules/admin/users"
import { useUrl } from "@/utils/url"

import { Button, Link as MuiLink, Stack } from "@mui/material"

import { AdminUserEditForm } from "./admin-user-edit-form"

export const AdminUserEditRoute: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const { activeUser, isAdminWriterUser, isOrgAdminUser } = useAuthentication()
  const { adminUserPath, adminUsersPath } = useUrl()
  const { userData, userId, userIsFetching, userIsLoading, userIsUnavailable } =
    useAdminUserPageState()

  const canMutateUser = canMutateAdminUser({
    isAdminWriterUser,
    isOrgAdminUser,
    targetRole: userData?.role,
  })
  const canDeleteUser = canDeleteAdminUser({
    actorId: activeUser?.id,
    isAdminWriterUser,
    isOrgAdminUser,
    targetId: userId,
    targetRole: userData?.role,
  })

  if (
    userIsUnavailable ||
    (!userIsLoading && userData != null && !canMutateUser)
  ) {
    return <Page404 />
  }

  if (userIsLoading) {
    return (
      <Stack sx={{ gap: 2 }}>
        <PageTitle
          breadcrumbActions={
            <>
              <MuiLink component={Link} to={adminUsersPath} underline="hover">
                {t("admin.users")}
              </MuiLink>
              {userId != null ? (
                <MuiLink
                  component={Link}
                  to={adminUserPath(String(userId))}
                  underline="hover"
                >
                  {t("admin.userDetail.viewUser")}
                </MuiLink>
              ) : null}
            </>
          }
          heading={t("editUser.title")}
          primaryActions={
            <Button
              onClick={() => {
                void navigate(adminUserPath(String(userId)), { replace: true })
              }}
              variant="outlined"
            >
              {t("button.cancel")}
            </Button>
          }
        />
        <PageCard>
          <AdminEntityFieldsSkeleton
            ariaLabel={t("loadingAriaLabel", {
              title: t("admin.userDetail.title"),
            })}
            roundedInputs
          />
        </PageCard>
      </Stack>
    )
  }

  if (!userData || userId === undefined) {
    return null
  }

  return (
    <AdminUserEditForm
      canDeleteUser={canDeleteUser}
      user={userData}
      userId={userId}
      userIsFetching={userIsFetching}
    />
  )
}
